import React from "react";

/**
 * Badge variants.
 * These map to color schemes like primary, brand, and secondary.
 */
export type BadgeVariant = "primary" | "brand" | "secondary" | "alert";

/**
 * Badge types.
 * - "number": Displays a number inside the badge.
 * - "dot": Displays a small circular indicator.
 */
export type BadgeType = "number" | "dot" | "both";

/**
 * Map variants to utility classes for styling.
 */
const variantClasses: Record<BadgeVariant, string> = {
  primary: `
    bg-[var(--color-semantic-bg-primary)]
    text-[var(--color-semantic-text-primary)]
  `,
  brand: `
    bg-[var(--semantic-bg-brand)]
    text-[var(--color-semantic-text-brand)]
  `,
  secondary: `
    bg-[var(--color-semantic-bg-secondary)]
    text-[var(--color-semantic-text-secondary)]
  `,
  alert: `
    bg-[var(--color-semantic-bg-danger-bold)]
    text-[var(--color-semantic-text-inverse-danger)]
`,
};

/**
 * Map badge types to utility classes for size and shape.
 */
const typeClasses: Record<BadgeType, string> = {
  number: `
    w-[20px]
    h-[20px]
    rounded-[var(--border-radius-sm)]
    flex items-center justify-center
    text-xs
  `,
  dot: `
    w-[8px]
    h-[8px]
    rounded-full
  `,
    both: `
    w-[20px]
    h-[20px]
    rounded-[var(--border-radius-sm)]
    flex items-center justify-center
    text-xs
    relative
  `,
};

/**
 * Badge Props
 */
export interface BadgeProps {
  /**
   * The variant of the badge (color scheme).
   * @default "primary"
   */
  variant?: BadgeVariant;

  /**
   * The type of the badge: "number" for numeric badges, "dot" for small indicators.
   * @default "number"
   */
  type?: BadgeType;

  /**
   * The number to display in the badge.
   * Ignored if `type` is "dot".
   */
  number?: number;

  /**
   * The child element (e.g., Button or Icon) around which the badge is wrapped.
   */
  children: React.ReactNode;
}
/**
 * Badge component using TypeScript.
 *
 * USAGE EXAMPLE:
 * ```tsx
 *  1.-  Badge with a number
 * <Badge variant="brand" type="number" number={22}>
 *   <button className="bg-gray-200 p-2 rounded">Button Label</button>
 * </Badge>
 *
 *  2.- Badge with a dot
 * <Badge variant="secondary" type="dot">
 *   <button className="bg-gray-200 p-2 rounded">Another Button</button>
 * </Badge>
 * ```
 *
 * PROPS:
 * - variant: Defines the color scheme of the badge. Options are "primary", "brand", or "secondary".
 *   - Default: "primary".
 * - type: Specifies the type of badge. Options are "number" (shows a number) or "dot" (small indicator).
 *   - Default: "number".
 * - number: A numeric value to display in the badge (only used if `type` is "number").
 * - children: The content or element wrapped by the badge (e.g., a Button or Icon).
 */

/**
 * Badge component.
 */
export const GenericBadge: React.FC<BadgeProps> = ({
  variant = "primary",
  type = "number",
  number = 0,
  children,
}) => {
  const isDot = type === "dot";
  const isBoth = type === "both";
  const showBadge = isDot || (type === "number" && number > 0) || isBoth;

  return (
    <div className="relative inline-flex items-center">
      {children}
      {showBadge && (
        <span
          className={`
            absolute top-[-10px] right-[-8px]
            ${variantClasses[variant]}
            ${typeClasses[type]}
          `}
        >
        {!isDot && number}
        </span>
      )}
    </div>
  );
};

export default GenericBadge;
